import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ThemeToggle } from "@/shared/components/theme/theme-toggle";
import { Button } from "@/shared/components/ui/button";
import { useServersStore } from "@/shared/stores/serversStore";
import { PacmanGame } from "@/features/landing/components/pacman/PacmanGame";
import { ServerGrid } from "@/features/landing/components/ServerGrid";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";
import {
    Activity,
    Crown,
    ShieldCheck,
    Sparkles,
    Sword,
    Users,
} from "lucide-react";

const _communityPillars = [
    {
        icon: ShieldCheck,
        title: "Community sicura",
        description:
            "Moderazione attiva, regole chiare e supporto costante per mantenere un ambiente pulito.",
    },
    {
        icon: Sword,
        title: "Focus PVE",
        description:
            "Esperienze cooperative progettate per il teamplay, senza tossicita e senza caos inutile.",
    },
    {
        icon: Crown,
        title: "Server curati",
        description:
            "Stack monitorato in tempo reale, manutenzione continua e aggiornamenti ragionati.",
    },
];

export function LandingPage() {
    const { servers, loading, error, fetchServers } = useServersStore();
    const [discordOnline, setDiscordOnline] = useState<number | null>(null);
    const [discordOnlineAvailable, setDiscordOnlineAvailable] = useState(false);
    const discordInviteUrl =
        import.meta.env.VITE_DISCORD_INVITE_URL ||
        import.meta.env.VITE_DISCORD_INVITE ||
        "";

    useEffect(() => {
        fetchServers();
    }, [fetchServers]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            try {
                const data = await apiFetchJson<{
                    available?: boolean;
                    presence_count?: number | null;
                }>(API_ENDPOINTS.PUBLIC_DISCORD_ONLINE);

                if (cancelled) return;
                const available = Boolean(data?.available);
                const count =
                    typeof data?.presence_count === "number"
                        ? Math.max(0, Math.floor(data.presence_count))
                        : null;

                setDiscordOnlineAvailable(available && count !== null);
                setDiscordOnline(count);
            } catch {
                if (cancelled) return;
                setDiscordOnlineAvailable(false);
                setDiscordOnline(null);
            }
        };

        void run();
        const t = window.setInterval(run, 60_000);
        return () => {
            cancelled = true;
            window.clearInterval(t);
        };
    }, []);

    const onlineServers = useMemo(
        () =>
            Array.isArray(servers)
                ? servers.filter((server) => server.status === "online")
                : [],
        [servers],
    );

    const totalPlayers = useMemo(
        () =>
            onlineServers.reduce(
                (sum, server) => sum + Number(server.players?.current || 0),
                0,
            ),
        [onlineServers],
    );

    const openDiscord = () => {
        const url = discordInviteUrl.trim();
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-background text-foreground transition-colors">
            <div className="landing-grid-bg pointer-events-none absolute inset-0 opacity-20 dark:opacity-15" />
            <div className="pointer-events-none absolute -left-24 top-12 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl dark:bg-orange-500/30" />
            <div className="pointer-events-none absolute right-0 top-52 h-96 w-96 rounded-full bg-cyan-400/15 blur-3xl dark:bg-cyan-400/25" />

            <header className="sticky top-0 z-50 border-b border-border/70 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logo.png"
                            alt="Un Mondo Marrano"
                            className="h-11 w-11 rounded-full border-2 border-orange-400 shadow-[0_0_24px_rgba(249,115,22,0.4)]"
                        />
                        <div>
                            <p className="font-arcade text-[10px] uppercase tracking-[0.35em] text-orange-500">
                                Community italiana
                            </p>
                            <p className="font-gaming text-xl">
                                Un Mondo Marrano
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-200 md:flex">
                            <Activity className="h-3.5 w-3.5" />
                            {totalPlayers} giocatori online
                        </div>
                        <ThemeToggle />
                        <Button
                            onClick={openDiscord}
                            disabled={!discordInviteUrl.trim()}
                            className="bg-gradient-to-r from-orange-500 to-amber-400 font-semibold text-black hover:from-orange-400 hover:to-amber-300"
                        >
                            <Users className="mr-2 h-4 w-4" />
                            Entra su Discord
                        </Button>
                    </div>
                </div>
            </header>

            <main>
                <section className="relative px-4 pb-8 pt-10 sm:pt-16">
                    <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                        >
                            <p className="font-arcade text-xs uppercase tracking-[0.5em] text-orange-500">
                                Stagione live 2026
                            </p>
                            <h1 className="mt-5 font-gaming text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
                                La tua arena
                                <span className="block bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-300 bg-clip-text text-transparent">
                                    PVE definitiva
                                </span>
                            </h1>
                            <p className="mt-6 max-w-xl text-base text-muted-foreground sm:text-lg">
                                Server reali monitorati 24/7, onboarding rapido,
                                eventi tematici e nessuno stato finto: solo dati
                                runtime dal cluster.
                            </p>

                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <Button
                                    onClick={openDiscord}
                                    size="lg"
                                    disabled={!discordInviteUrl.trim()}
                                    className="bg-gradient-to-r from-orange-500 to-amber-400 px-7 font-semibold text-black hover:from-orange-400 hover:to-amber-300"
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Entra ora
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={() =>
                                        window.scrollTo({
                                            top: window.innerHeight * 0.88,
                                            behavior: "smooth",
                                        })
                                    }
                                    className="border-border/80 bg-background/40 hover:bg-muted/50"
                                >
                                    Vedi i server attivi
                                </Button>
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                <div className="rounded-xl border border-border/60 bg-card/70 p-3 backdrop-blur-sm">
                                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                        Server online
                                    </p>
                                    <p className="font-gaming text-2xl">
                                        {onlineServers.length}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-border/60 bg-card/70 p-3 backdrop-blur-sm">
                                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                        Giocatori live
                                    </p>
                                    <p className="font-gaming text-2xl">
                                        {totalPlayers}
                                    </p>
                                </div>
                                <div className="col-span-2 rounded-xl border border-border/60 bg-card/70 p-3 backdrop-blur-sm sm:col-span-1">
                                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                        Discord online
                                    </p>
                                    <div className="flex items-baseline gap-2">
                                        <p className="font-gaming text-2xl text-emerald-600 dark:text-emerald-300">
                                            {discordOnlineAvailable
                                                ? discordOnline
                                                : "N/D"}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {discordOnlineAvailable
                                                ? "membri online"
                                                : "widget non disponibile"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.7 }}
                            className="relative"
                        >
                            <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-orange-500/30 to-cyan-400/20 blur-2xl dark:from-orange-500/40 dark:to-cyan-400/30" />
                            <div className="relative rounded-3xl border border-border/70 bg-card/55 p-4 backdrop-blur-2xl">
                                <PacmanGame
                                    width={760}
                                    height={620}
                                    className="w-full"
                                />
                            </div>
                        </motion.div>
                    </div>
                </section>

                <ServerGrid
                    servers={onlineServers}
                    loading={loading}
                    error={error}
                />

                <section className="px-4 pb-20">
                    <div className="mx-auto max-w-7xl">
                        <motion.div
                            className="mb-8 text-center"
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <p className="font-arcade text-xs uppercase tracking-[0.45em] text-orange-500">
                                DNA della community
                            </p>
                            <h2 className="mt-4 font-gaming text-4xl sm:text-5xl">
                                Zero drama, massimo teamplay
                            </h2>
                        </motion.div>

                        <div className="grid gap-5 md:grid-cols-3">
                            {_communityPillars.map((pillar, index) => (
                                <motion.article
                                    key={pillar.title}
                                    className="rounded-2xl border border-border/70 bg-card/70 p-6 backdrop-blur-xl"
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.45,
                                        delay: index * 0.08,
                                    }}
                                    viewport={{ once: true }}
                                >
                                    <pillar.icon className="h-8 w-8 text-orange-500" />
                                    <h3 className="mt-4 font-gaming text-2xl">
                                        {pillar.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                                        {pillar.description}
                                    </p>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
